import {
  activeSearchFilterCount,
  channelRoute,
  DEFAULT_SEARCH_QUERY,
  effectiveSearchQuery,
  feedScrape,
  formatRoute,
  isSearchPagingExhausted,
  readSearchOperators,
  SEARCH_DATES,
  SEARCH_DURATIONS,
  SEARCH_FEATURES,
  SEARCH_FIRST_PAGE,
  SEARCH_SORTS,
  SEARCH_TYPES,
  searchRoute,
  type FeedItem,
  type ParsedList,
  type Result,
  type SearchDate,
  type SearchDuration,
  type SearchFeature,
  type SearchOperator,
  type SearchQuery,
  type SearchSort,
  type SearchType,
} from '@visious/core';
import { describeProblem, type ProblemCopy } from '../../api/problem';
import { text } from '../../strings';
import type { ApiProblem } from '../../api/videos';
import { toListingRows, type ListingRow, type ProgressLookup } from '../../listing/model';
import type { MenuModel } from '../../ui/menuModel';

/**
 * §8's Search page, as reasoning. R13: no DOM, no React, no `RefObject` — `useSearch.ts` feeds this and
 * `Search.tsx` renders what it returns.
 *
 * The page has two halves and they fail differently, which is why they are two functions rather than
 * one state machine. The **controls** are a function of the query alone and are always renderable, even
 * while the request is in flight or has failed; the **results** are a function of the response. Folding
 * them together would mean a failed search hides its own filter bar, so the reader cannot change the
 * filter that failed.
 */

// Re-exported so `view.tsx` and `Search.tsx` can name what they render without importing core
// (non-negotiable 2 — a type-only import is still an import, and it is the edge that keeps the Android
// path open). The depcruise rule catches it, which is how this line came to exist.
export type { SearchQuery };

/* ------------------------------------------------------------------------------------------------
 * The controls
 * ---------------------------------------------------------------------------------------------- */

/** R7 lives here: core ships the values, the platform ships the words (`strings/en/search.ts`). */
function typeLabels(): Readonly<Record<SearchType, string>> {
  return text().search.type;
}

function sortLabels(): Readonly<Record<SearchSort, string>> {
  return text().search.sort;
}

function dateLabels(): Readonly<Record<SearchDate, string>> {
  const words = text().search.date;
  return {
    any: words.any,
    hour: words.hour,
    today: words.today,
    week: words.week,
    month: words.month,
    year: words.year,
  };
}

function durationLabels(): Readonly<Record<SearchDuration, string>> {
  const words = text().search.duration;
  return { any: words.any, short: words.short, medium: words.medium, long: words.long };
}

function durationDetails(): Readonly<Record<SearchDuration, string | null>> {
  const words = text().search.duration;
  return {
    any: null,
    short: words.shortDetail,
    medium: words.mediumDetail,
    long: words.longDetail,
  };
}

// The sort menu's note and the sparse-features details ("rarely any results") were removed:
// the measurements behind them live on in `docs/api/search.md` §7, but the menus now state only the
// options themselves. The duration details stay — they define what Short/Medium/Long *mean*, which is
// information the reader cannot get anywhere else, rather than commentary on the option list.

function featureLabels(): Readonly<Record<SearchFeature, string>> {
  const words = text().search.feature;
  return {
    hd: words.hd,
    subtitles: words.subtitles,
    creative_commons: words.creativeCommons,
    '3d': words.threeD,
    live: words.live,
    purchased: words.purchased,
    '4k': words.fourK,
    '360': words.threeSixty,
    location: words.location,
    hdr: words.hdr,
  };
}

export type FilterMenuId = 'type' | 'sort' | 'date' | 'duration' | 'features';

export interface FilterMenu {
  readonly id: FilterMenuId;
  readonly menu: MenuModel;
  readonly multiSelect: boolean;
  readonly disabled: boolean;
  readonly title: string | null;
}

export interface SearchControls {
  /** All five filters as menus — `type` first, since it is the primary axis. */
  readonly menus: readonly FilterMenu[];
  /** How many filters are set away from their defaults — 0 hides the reset. */
  readonly activeFilters: number;
  readonly resetHref: string;
  /**
   * Present when `q` carries an operator: what it does to the other controls, in a sentence. This is
   * §8's "with the reason attached", and the reason is not the one the API reference gave — see
   * `effectiveSearchQuery`.
   */
  readonly suppression: SuppressionNotice | null;
}

export interface SuppressionNotice {
  readonly operators: readonly SearchOperator[];
  /** For the notice above the results. */
  readonly text: string;
  /** For each disabled control's `title`, so the reason is there before the click. */
  readonly short: string;
  /**
   * Somewhere real to go, because "your filters were switched off" leaves the reader holding a query
   * they cannot refine. Two different destinations for two different reasons:
   *
   * - `subscriptions:` cannot be answered by this endpoint at all — the API route never passes a user
   *   into the query processor, so it comes back `200 []`. The subscriptions feed is the answer.
   * - `channel:UC…` *works*, but only as a whole-channel search with no filters. Measured:
   *   `/api/v1/channels/{ucid}/search?q=` returns exactly the same rows **and ignores every filter**
   *   rather than corrupting them, so the channel page is the place where browsing one channel is a
   *   first-class thing to do instead of an operator that disables half the page. That link could not
   *   be offered before 2d, because there was no channel page to send anyone to.
   */
  readonly deadEnd: { readonly text: string; readonly label: string; readonly href: string } | null;
}

const OPERATOR_NAMES: Readonly<Record<SearchOperator, string>> = {
  channel: 'channel:',
  user: 'user:',
  subscriptions: 'subscriptions:',
};

function suppressionFor(query: SearchQuery): SuppressionNotice | null {
  const { operators, filtersSuppressed, channelId } = readSearchOperators(query.q);
  if (!filtersSuppressed) return null;

  const names = operators.map((operator) => OPERATOR_NAMES[operator]).join(' and ');
  // `channel:` and `user:` scope a search to one channel; `subscriptions:` cannot be answered here at
  // all. The two need different sentences, and writing one for both was a defect a driven run caught:
  // a `subscriptions:true` query was being told its filters would "mix in other channels' videos",
  // which is a claim about a search that never happens.
  const scoping = operators.includes('channel') || operators.includes('user');
  const deadEnd = operators.includes('subscriptions');
  const words = text().search.suppression;

  return {
    operators,
    // Measured, and worth stating precisely: the filters are not ignored, they *corrupt* the answer.
    // `channel:UC… black hole` returns 28 rows all from that channel; adding `duration=long` returns
    // 19 rows from 10 channels, 1 of them the one asked for.
    text: scoping ? words.scoping(names) : words.special(names),
    short: scoping ? words.scopingShort(names) : words.specialShort(names),
    deadEnd: deadEnd
      ? {
          text: words.subscriptions,
          label: words.subscriptionsAction,
          href: formatRoute({ kind: 'feed', feed: 'subscriptions' }),
        }
      : channelId === null
        ? null
        : {
            // Only what the channel page actually has: the tabs and their paging are built; a
            // search box *inside* a channel is not built, and the notice must not promise it.
            text: words.channel,
            label: words.channelAction,
            href: formatRoute(channelRoute(channelId)),
          },
  };
}

function itemsOf<T extends string>(
  values: readonly T[],
  labels: Readonly<Record<T, string>>,
  current: T,
  details?: Readonly<Record<T, string | null>>,
) {
  return values.map((value) => ({
    id: value,
    label: labels[value],
    detail: details?.[value] ?? null,
    selected: value === current,
  }));
}

export function searchControls(query: SearchQuery): SearchControls {
  const suppression = suppressionFor(query);
  // Rendered from the **effective** query, so a suppressed control shows the value that is actually in
  // force rather than the one that was asked for and dropped. The URL still carries the asked-for one
  // (`links/routes.ts`), which is what makes deleting the operator restore the reader's choices.
  const shown = effectiveSearchQuery(query);
  const disabled = suppression !== null;
  const title = suppression?.short ?? null;

  const words = text().search;
  const anyValue = words.anyValue;
  const sorts = sortLabels();
  const dates = dateLabels();
  const durations = durationLabels();
  const features = featureLabels();

  // Sort last (it sits on the row's right edge — `Search.tsx` pushes it there, and the DOM
  // order has to match the visual one for a keyboard). `valueActive` per menu is "set away from its
  // default", which is what earns the trigger's value the accent.
  const menus: FilterMenu[] = [
    {
      id: 'type',
      multiSelect: false,
      disabled,
      title,
      menu: {
        label: words.menu.type,
        value: typeLabels()[shown.type],
        valueActive: shown.type !== DEFAULT_SEARCH_QUERY.type,
        sections: [{ title: null, items: itemsOf(SEARCH_TYPES, typeLabels(), shown.type) }],
        note: null,
      },
    },
    {
      id: 'date',
      multiSelect: false,
      disabled,
      title,
      menu: {
        label: words.menu.date,
        // `ANY` on the trigger rather than the item's own label: the trigger already shows the menu's
        // name, so "Upload date Any time" and "Length Any length" say the noun twice. Inside the menu
        // the long form is right, because there the name is a heading rather than a prefix.
        value: shown.date === 'any' ? anyValue : dates[shown.date],
        valueActive: shown.date !== DEFAULT_SEARCH_QUERY.date,
        sections: [{ title: null, items: itemsOf(SEARCH_DATES, dates, shown.date) }],
        note: null,
      },
    },
    {
      id: 'duration',
      multiSelect: false,
      disabled,
      title,
      menu: {
        label: words.menu.duration,
        value: shown.duration === 'any' ? anyValue : durations[shown.duration],
        valueActive: shown.duration !== DEFAULT_SEARCH_QUERY.duration,
        sections: [
          {
            title: null,
            items: itemsOf(SEARCH_DURATIONS, durations, shown.duration, durationDetails()),
          },
        ],
        note: null,
      },
    },
    {
      id: 'features',
      multiSelect: true,
      disabled,
      title,
      menu: {
        label: words.menu.features,
        value:
          shown.features.length === 0
            ? anyValue
            : shown.features.map((feature) => features[feature]).join(', '),
        valueActive: shown.features.length > 0,
        sections: [
          {
            title: null,
            items: SEARCH_FEATURES.map((feature) => ({
              id: feature,
              label: features[feature],
              detail: null,
              selected: shown.features.includes(feature),
            })),
          },
        ],
        note: null,
      },
    },
    {
      id: 'sort',
      multiSelect: false,
      disabled,
      title,
      menu: {
        label: words.menu.sort,
        value: sorts[shown.sort],
        valueActive: shown.sort !== DEFAULT_SEARCH_QUERY.sort,
        sections: [{ title: null, items: itemsOf(SEARCH_SORTS, sorts, shown.sort) }],
        note: null,
      },
    },
  ];

  return {
    menus,
    activeFilters: activeSearchFilterCount(shown),
    resetHref: formatRoute(searchRoute(query.q)),
    suppression,
  };
}

/**
 * The route for "the same search with this filter changed". Page resets, because page 4 of *videos* is
 * not page 4 of *playlists* — the bug that is invisible until someone pages deep and then narrows.
 */
export function withFilter(query: SearchQuery, change: Partial<Omit<SearchQuery, 'q' | 'page'>>): {
  readonly kind: 'search';
  readonly search: SearchQuery;
} {
  return {
    kind: 'search',
    search: { ...query, ...change, page: SEARCH_FIRST_PAGE },
  };
}

/** Toggling one `features` entry, kept in the canonical order so two paths to one set share a URL. */
export function toggleFeature(
  query: SearchQuery,
  feature: SearchFeature,
): { readonly kind: 'search'; readonly search: SearchQuery } {
  const next = query.features.includes(feature)
    ? query.features.filter((existing) => existing !== feature)
    : SEARCH_FEATURES.filter((existing) => existing === feature || query.features.includes(existing));
  return withFilter(query, { features: next });
}

/** The href for a menu item, so `Search.tsx` builds no URLs (R13). */
export function menuItemHref(query: SearchQuery, menu: FilterMenuId, itemId: string): string {
  switch (menu) {
    case 'type':
      return formatRoute(withFilter(query, { type: itemId as SearchType }));
    case 'sort':
      return formatRoute(withFilter(query, { sort: itemId as SearchSort }));
    case 'date':
      return formatRoute(withFilter(query, { date: itemId as SearchDate }));
    case 'duration':
      return formatRoute(withFilter(query, { duration: itemId as SearchDuration }));
    case 'features':
      return formatRoute(toggleFeature(query, itemId as SearchFeature));
  }
}

/* ------------------------------------------------------------------------------------------------
 * The results
 * ---------------------------------------------------------------------------------------------- */

export interface SearchNotice {
  readonly id: 'suppressed' | 'dead-end' | 'dropped' | 'unknown-types' | 'degenerate';
  readonly text: string;
  /** Somewhere to go instead, for a notice that is really a dead end. */
  readonly action: { readonly label: string; readonly href: string } | null;
}

export type SearchState =
  | { readonly kind: 'loading'; readonly skeletonCards: number }
  | {
      readonly kind: 'problem';
      readonly copy: ProblemCopy;
      readonly alternative: { readonly label: string; readonly href: string } | null;
    }
  /** Page 1 came back empty. A real answer, and the copy depends on which filters were on. */
  | {
      readonly kind: 'empty';
      readonly title: string;
      readonly detail: string;
      readonly notices: readonly SearchNotice[];
      readonly resetHref: string | null;
    }
  /**
   * A deep link *started* past the end. Not an error and not an empty result — the previous page
   * exists, and the only useful action is going back to it.
   */
  | { readonly kind: 'exhausted'; readonly page: number; readonly previousHref: string }
  | {
      readonly kind: 'ready';
      readonly rows: readonly ListingRow[];
      readonly notices: readonly SearchNotice[];
      /**
       * Whether another page may exist. `false` only on proof — the instance refused the next page
       * (`isSearchPagingExhausted`) or answered it with nothing. There is no total and no continuation
       * token, so "is there more" is only answerable by asking.
       */
      readonly more: boolean;
    };

/** 320 — the same number and the same reason as a feed card (`screens/feed/model.ts`). */
const CARD_THUMBNAIL_MIN_WIDTH = 320;
const SKELETON_CARDS = 12;

export interface SearchStateOptions {
  readonly nowEpochSec: number;
  /** 4b's stored playheads (`useWatchProgressLookup`). Only the video rows can carry one. */
  readonly progress: ProgressLookup;
}

/**
 * The whole listing, folded from every page fetched so far — `results[0]` is `query.page`, each next
 * entry the page after it. The pages *overlap* (2–5 of ~20 rows measured on `type=all`), so appended
 * repeats are dropped by row key; without that they would collide as React keys before they confused
 * anyone. Per-page `feedScrape`, because grading one page's scrape over another page's rows would let
 * a healthy page lend its durations to a degenerate one.
 */
export function searchStateFor(
  query: SearchQuery,
  results: readonly Result<ParsedList<FeedItem>, ApiProblem>[] | null,
  options: SearchStateOptions,
): SearchState {
  if (results === null || results.length === 0) {
    return { kind: 'loading', skeletonCards: SKELETON_CARDS };
  }

  const suppression = suppressionFor(query);
  const notices = noticesFor(suppression);

  const rows: ListingRow[] = [];
  const seen = new Set<string>();
  let end = false;
  let firstItems = 0;
  let firstDropped = 0;

  for (const [index, result] of results.entries()) {
    if (!result.ok) {
      const pageNumber = query.page + index;
      // The one failure that is not a failure: page N+1 of a result set that only had N.
      if (result.error.kind === 'transport' && isSearchPagingExhausted(result.error.error, pageNumber)) {
        end = true;
        break;
      }
      // Any other failed page is the page's problem, rows on screen or not — the alternative is a
      // silent Load more that does nothing (`useSubscriptions.ts` makes the same call). On the first
      // page this is also the only arm, so the two readings share one sentence honestly.
      return {
        kind: 'problem',
        copy: describeProblem(result.error, problemSubject()),
        alternative: null,
      };
    }

    const { items, dropped } = result.value;
    if (index === 0) {
      firstItems = items.length;
      firstDropped = dropped.length;
    }
    // `200 []` is the other spelling of "no more" — deep paging usually 400s, but an empty page is
    // just as much proof, and offering Load more against it would loop forever.
    if (items.length === 0) end = true;

    const scrape = feedScrape(
      items.flatMap((item) => (item.kind === 'video' ? [item.video] : [])),
      options.nowEpochSec,
    );
    const { rows: pageRows } = toListingRows(items, {
      nowEpochSec: options.nowEpochSec,
      thumbnailMinWidth: CARD_THUMBNAIL_MIN_WIDTH,
      // Search's `published` is back-computed from `publishedText` — every row carries the request's
      // own time of day — so a row gets the relative form and no absolute-date tooltip (§2).
      absoluteDates: false,
      scrape,
      progress: options.progress,
    });
    for (const row of pageRows) {
      if (seen.has(row.key)) continue;
      seen.add(row.key);
      rows.push(row);
    }
  }

  if (rows.length === 0) {
    // An empty page 1 with drops is not an empty result — it is a result we could not read, and saying
    // "nothing matched" about it would be the R4 count dying one layer above the parser.
    if (firstDropped > 0 && firstItems === 0) {
      return {
        kind: 'problem',
        copy: {
          title: text().search.allDropped.title,
          detail: text().search.allDropped.detail,
          retryable: true,
        },
        alternative: null,
      };
    }

    if (query.page > SEARCH_FIRST_PAGE) {
      return {
        kind: 'exhausted',
        page: query.page,
        previousHref: formatRoute({ kind: 'search', search: { ...query, page: query.page - 1 } }),
      };
    }

    const filtered = activeSearchFilterCount(effectiveSearchQuery(query)) > 0;
    return {
      kind: 'empty',
      title: text().search.empty.title(query.q),
      // A dead-end operator gets neither piece of advice. Both are about a search that *ran* and found
      // nothing, and telling someone to check their spelling when the endpoint structurally cannot
      // answer their query is the page blaming the reader for its own limitation — caught in a driven
      // run, where `subscriptions:true linux` was offered exactly that.
      detail:
        suppression?.deadEnd !== null && suppression?.deadEnd !== undefined
          ? text().search.empty.deadEnd
          : filtered
            ? text().search.empty.filtered
            : text().search.empty.plain,
      notices,
      resetHref: filtered ? formatRoute(searchRoute(query.q)) : null,
    };
  }

  return { kind: 'ready', rows, notices, more: !end };
}

/**
 * The suppression pair, and nothing else any more.
 *
 * Three notices used to sit under it — rows of a type this build cannot show, rows that could not be
 * read, and a page whose rows carried titles only. All three reported on the *response*; suppression
 * reports on the *controls in front of the reader*, which is why it survives: the filter bar below it
 * is switched off and this is the only thing on the page that says why.
 */
function noticesFor(suppression: SuppressionNotice | null): readonly SearchNotice[] {
  if (suppression === null) return [];

  const notices: SearchNotice[] = [{ id: 'suppressed', text: suppression.text, action: null }];
  if (suppression.deadEnd !== null) {
    notices.push({
      id: 'dead-end',
      text: suppression.deadEnd.text,
      action: { label: suppression.deadEnd.label, href: suppression.deadEnd.href },
    });
  }

  return notices;
}

function problemSubject(): {
  readonly notFound: ProblemCopy;
  readonly unreadable: (error: { entity: string; field: string | null; reason: string }) => ProblemCopy;
  readonly disabled: ProblemCopy;
} {
  const words = text().search.problem;
  return {
    notFound: words.notFound,
    unreadable: () => words.unreadable,
    disabled: words.disabled,
  };
}
